import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { BrandMark } from "./BrandMark";
import { ThemeToggle } from "./ThemeToggle";
import { Check, ChevronDown, CircleAlert, CircleCheck, ClipboardCheck, Clock3, Compass, ExternalLink, FilePenLine, GitBranch, GitPullRequest, Hand, Info, Layers, MessageSquareText, MoreHorizontal, X } from "lucide-react";
import {
  buildTourStops,
  compileCodeMap,
  resolveGuideItem,
  type CodeMapCompilation,
} from "../../shared/codemap";
import { defaultChewConfig, type ChewConfig } from "../../shared/config";
import type { Glossary } from "../../shared/glossary";
import { reviewPresentation, summaryItems, type GuideLayer, type ReviewGuide } from "../../shared/guide";
import type { LayerStatus, ProgressProjection, ReviewVerdict } from "../../shared/progress";
import type { ReviewAccess } from "../../shared/review-access";
import { cacheFiles, cacheGuide, db, draftCommentKind, ensureSession, lineAnchorForDraft, resetReview, sessionIdFor, type ReviewLayerViewState } from "../lib/db";
import {
  loadReviewBundle,
  type FixtureDescriptor,
  type FixtureFiles,
} from "../lib/fixtures";
import { handleLocalNavigation, navigate } from "../lib/navigation";
import { checksSignal, commentsSignal } from "../lib/pull-signals";
import { attestHostedAuthorSweep, hostedInlineComment, publishHostedReview, replyToHostedReviewThread, type HostedPreview, type HostedReviewContext } from "../lib/hosted";
import { hasExplicitReviewPosition, readReviewPosition, reviewLandingPath, writeReviewPosition } from "../lib/review-route";
import { rememberStackContext } from "../lib/review-stack";
import { guideAuthoringPath } from "../../shared/pull-routes";
import { useTerraformPanel } from "../hooks/useTerraformPanel";
import { prefetchTerraformSelection } from "../hooks/useTerraformPanelData";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { useReviewSession } from "../hooks/useReviewSession";
import { useActiveReviewTime } from "../hooks/useActiveReviewTime";
import { ReviewReceiptView } from "./CoverageReceipt";
import { PullBadge } from "./PullPresentation";
import { repositoryShortName } from "../lib/pull-view";
import { AppNavigationControls } from "./AppNavigationControls";
import { ChangeProfile } from "./ChangeProfile";
import { LayerView } from "./LayerView";
import { ReviewSkeleton } from "./LoadingSkeleton";
import { MermaidDiagram } from "./MermaidDiagram";
import type { ChewCodeTarget } from "./ChewMarkdown";
import { GuideProse } from "./GuideProse";
import { OverviewCard } from "./OverviewCard";
import { ProgressHeader } from "./ProgressHeader";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "./ui/popover";
import { Textarea } from "./ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface LoadedState {
  config: ChewConfig;
  glossary?: Glossary;
  guide: ReviewGuide;
  files: FixtureFiles;
  compilation: CodeMapCompilation;
  source: "network" | "cache" | "github";
}

type ReviewScreen = "intro" | "layer" | "complete";
type HostedPullDetails = HostedPreview["pull"];

const verdictOptions: Array<{
  id: ReviewVerdict;
  key: string;
  label: string;
  description: string;
}> = [
  { id: "approve", key: "A", label: "Approve", description: "Ready to merge for this review requirement." },
  { id: "request-changes", key: "X", label: "Request changes", description: "Code or design must change before approval." },
  { id: "comment", key: "C", label: "Comment only", description: "Questions or non-blocking feedback; no verdict." },
  { id: "not-ready", key: "N", label: "Return as not ready", description: "Stop here and request changes for missing context, evidence, checks, or a coherent slice." },
];

function displayDate(value?: string): string {
  if (!value) return "Unknown";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function CiIcon({ state }: { state: NonNullable<HostedReviewContext>["ci"]["state"] }) {
  if (state === "success") return <Check aria-hidden="true" />;
  if (state === "failure") return <X aria-hidden="true" />;
  if (state === "pending") return <Clock3 aria-hidden="true" />;
  return <CircleAlert aria-hidden="true" />;
}

function reviewResolutionLabel(review?: HostedReviewContext["resolution"]["review"]): string {
  if (!review) return "Reviews loading";
  if (review.state === "changes-requested") return "Changes requested";
  if (review.state === "approved") return "Approved";
  if (review.state === "not-required") return "No approval required";
  if (review.requiredApprovals !== null && review.requiredApprovals > 0) {
    return `${review.approvals}/${review.requiredApprovals} approved`;
  }
  return review.state === "unavailable" ? "Review status unavailable" : `${review.approvals} approved`;
}

function reviewResolutionTone(review?: HostedReviewContext["resolution"]["review"]): string {
  if (!review || review.state === "unavailable") return "neutral";
  if (review.state === "approved" || review.state === "not-required") return "success";
  if (review.state === "changes-requested") return "failure";
  return "pending";
}

function mergeResolutionLabel(merge?: HostedReviewContext["resolution"]["merge"]): string {
  if (!merge) return "Merge status loading";
  if (merge.draft) return "Draft";
  if (merge.state === "clean") return "Mergeable";
  if (merge.state === "dirty") return "Merge conflicts";
  if (merge.state === "behind") return "Behind base";
  if (merge.state === "blocked") return "Merge blocked";
  if (merge.state === "unstable") return "Checks outstanding";
  return merge.mergeable === true ? "Mergeable" : merge.mergeable === false ? "Not mergeable" : "Merge status pending";
}

function mergeResolutionTone(merge?: HostedReviewContext["resolution"]["merge"]): string {
  if (!merge) return "neutral";
  if (merge.draft || ["blocked", "behind", "unstable"].includes(merge.state)) return "pending";
  if (merge.state === "dirty" || merge.mergeable === false) return "failure";
  if (merge.state === "clean" || merge.mergeable === true) return "success";
  return "neutral";
}

function mergeResolutionShortLabel(merge?: HostedReviewContext["resolution"]["merge"]): string {
  if (!merge) return "Pending";
  if (merge.draft) return "Draft";
  if (merge.state === "behind") return "Behind";
  if (merge.state === "blocked") return "Blocked";
  if (merge.state === "unstable") return "Waiting";
  if (merge.state === "dirty" || merge.mergeable === false) return "Conflict";
  if (merge.state === "clean" || merge.mergeable === true) return "Ready";
  return "Pending";
}

function PullContextDisclosure({
  context,
  pull,
}: {
  context?: HostedReviewContext;
  pull: HostedPullDetails;
}) {
  const ci = context?.ci;
  const ciLabel = ci ? checksSignal(ci).full : "Checks loading";
  const changed = context?.changedSinceReview;
  const conversation = context?.conversation;
  const review = context?.resolution.review;
  const merge = context?.resolution.merge;
  const reviewLabel = reviewResolutionLabel(review);
  const mergeLabel = mergeResolutionLabel(merge);
  const mergeShortLabel = mergeResolutionShortLabel(merge);
  const reviewCountLabel = !review || review.state === "unavailable" ? "–"
    : review.requiredApprovals !== null && review.requiredApprovals > 0 ? `${review.approvals}/${review.requiredApprovals}`
      : review.state === "not-required" ? "n/a" : String(review.approvals);
  const ciCountLabel = !ci || ci.state === "unavailable" ? "–"
    : ci.state === "failure" ? String(ci.failed)
      : ci.state === "pending" ? String(ci.pending)
        : ci.total > 0 ? String(ci.total) : "–";
  const conversationLabel = conversation
    ? commentsSignal({
      issueComments: conversation.issueCommentCount,
      reviewThreads: conversation.reviewThreadCount,
      unresolvedThreads: conversation.unresolvedThreadCount,
    }).full
    : "Comments loading";
  const conversationTone = !conversation ? "neutral"
    : conversation.unresolvedThreadCount === 0 ? "success"
      : merge?.conversationResolutionRequired ? "failure" : "pending";
  const tones = [reviewResolutionTone(review), ci?.state ?? "neutral", conversationTone, mergeResolutionTone(merge)];
  const readinessTone = tones.includes("failure") ? "failure"
    : tones.includes("pending") || tones.includes("neutral") ? "pending" : "success";
  const readinessLabel = readinessTone === "failure" ? "Needs attention"
    : readinessTone === "pending" ? "Not ready yet" : "Ready to merge";
  const failedChecks = ci?.checks.filter((check) => check.state === "failure") ?? [];
  const pendingChecks = ci?.checks.filter((check) => check.state === "pending") ?? [];
  const unresolvedComments = conversation?.recent.filter((item) => item.kind === "review-thread" && !item.resolved) ?? [];
  const displayedUnresolvedComments = unresolvedComments.slice(0, 3);
  const mergeNeedsAttention = Boolean(merge && (merge.state === "dirty" || merge.state === "behind" || merge.state === "blocked" || merge.mergeable === false));
  const reviewNeedsAttention = review?.state === "changes-requested" || review?.state === "review-required";
  const hasAction = failedChecks.length > 0 || review?.state === "changes-requested" || (conversation?.unresolvedThreadCount ?? 0) > 0 || mergeNeedsAttention;
  return (
    <Popover>
      <PopoverTrigger
        render={<button aria-label={`Open pull request readiness. Reviews: ${reviewLabel}. Checks: ${ciLabel}. Conversations: ${conversationLabel}. Merge: ${mergeLabel}.`} className="readiness-summary" data-readiness-tone={readinessTone} title={`${readinessLabel}. Open details`} type="button" />}
      >
        <span className={`readiness-primary signal-${readinessTone}`}>
          {readinessTone === "success" ? <CircleCheck aria-hidden="true" /> : <CircleAlert aria-hidden="true" />}
          <strong>{readinessLabel}</strong>
        </span>
        <span className={`readiness-signal readiness-reviews signal-${reviewResolutionTone(review)}`} title={`Reviews: ${reviewLabel}`}>
          <ClipboardCheck aria-hidden="true" /><small>{reviewCountLabel}</small>
        </span>
        <span className={`readiness-signal readiness-checks signal-${ci?.state ?? "neutral"}`} title={`Checks: ${ciLabel}`}>
          {ci ? <CiIcon state={ci.state} /> : <span className="ci-loading-dot" />}<small>{ciCountLabel}</small>
        </span>
        <span className={`readiness-signal readiness-conversations signal-${conversationTone}`} title={`Conversations: ${conversationLabel}`}>
          <MessageSquareText aria-hidden="true" /><small>{conversation?.unresolvedThreadCount ?? "–"}</small>
        </span>
        <span className={`readiness-signal readiness-merge signal-${mergeResolutionTone(merge)}`} title={`Merge: ${mergeLabel}`}>
          <GitPullRequest aria-hidden="true" /><small className="readiness-signal-label">{mergeShortLabel}</small>
        </span>
      </PopoverTrigger>
      <PopoverContent align="end" className="pull-context-popover" data-readiness-tone={readinessTone} side="bottom" sideOffset={7}>
        <PopoverHeader className="pull-context-popover-header">
          <div className={`pull-context-status-mark signal-${readinessTone}`}>
            {readinessTone === "success" ? <CircleCheck aria-hidden="true" /> : <CircleAlert aria-hidden="true" />}
          </div>
          <div>
            <PopoverTitle>{readinessLabel}</PopoverTitle>
            <PopoverDescription>#{pull.number} · opened by @{pull.author} · {pull.changedFiles} files</PopoverDescription>
          </div>
        </PopoverHeader>

        <div className="readiness-overview">
          <article className={`signal-${reviewResolutionTone(review)}`}><ClipboardCheck aria-hidden="true" /><span><small>Review</small><strong>{reviewLabel}</strong></span></article>
          <article className={`signal-${ci?.state ?? "neutral"}`}><span>{ci ? <CiIcon state={ci.state} /> : <span className="ci-loading-dot" />}</span><span><small>Checks</small><strong>{ciLabel}</strong></span></article>
          <article className={`signal-${conversationTone}`}><MessageSquareText aria-hidden="true" /><span><small>Feedback</small><strong>{conversation ? `${conversation.unresolvedThreadCount} unresolved` : "Loading"}</strong></span></article>
          <article className={`signal-${mergeResolutionTone(merge)}`}><GitPullRequest aria-hidden="true" /><span><small>Merge</small><strong>{mergeLabel}</strong></span></article>
        </div>

        <div className="pull-context-popover-body">
          <section className="pull-context-priorities">
            <header><span>{hasAction ? "What needs attention" : readinessTone === "success" ? "All clear" : "What we're waiting for"}</span>{hasAction && <small>Resolve before merge</small>}</header>
            {failedChecks.map((check) => (
              check.url ? <a className="pull-context-action action-failure" href={check.url} key={check.id} rel="noreferrer" target="_blank">
                <X aria-hidden="true" /><span><strong>{check.name}</strong><small>Failed check · open run</small></span><ExternalLink aria-hidden="true" />
              </a> : <div className="pull-context-action action-failure" key={check.id}><X aria-hidden="true" /><span><strong>{check.name}</strong><small>Failed check</small></span></div>
            ))}
            {review?.state === "changes-requested" && <div className="pull-context-action action-failure">
              <ClipboardCheck aria-hidden="true" /><span><strong>Changes requested</strong><small>{review.changesRequestedBy.map((login) => `@${login}`).join(", ") || "A reviewer requested changes"}</small></span>
            </div>}
            {review?.state === "review-required" && <div className="pull-context-action action-pending">
              <ClipboardCheck aria-hidden="true" /><span><strong>Approval still needed</strong><small>{review.requestedReviewers.length > 0 ? `Waiting for ${review.requestedReviewers.map((login) => `@${login}`).join(", ")}` : review.codeOwnerReviewRequired ? "Code-owner approval required" : reviewLabel}</small></span>
            </div>}
            {displayedUnresolvedComments.map((item) => (
              <a className={`pull-context-action ${merge?.conversationResolutionRequired ? "action-failure" : "action-pending"}`} href={item.url} key={item.url} rel="noreferrer" target="_blank">
                <MessageSquareText aria-hidden="true" /><span><strong>@{item.author}</strong><small>{item.body || "Open review comment"}{item.path ? ` · ${item.path}${item.line ? `:${item.line}` : ""}` : ""}</small></span><ExternalLink aria-hidden="true" />
              </a>
            ))}
            {(conversation?.unresolvedThreadCount ?? 0) > displayedUnresolvedComments.length && <a className="pull-context-more" href={pull.url} rel="noreferrer" target="_blank">+{conversation!.unresolvedThreadCount - displayedUnresolvedComments.length} more unresolved on GitHub <ExternalLink aria-hidden="true" /></a>}
            {mergeNeedsAttention && <a className="pull-context-action action-failure" href={pull.url} rel="noreferrer" target="_blank">
              <GitPullRequest aria-hidden="true" /><span><strong>{mergeLabel}</strong><small>Open the pull request to resolve merge blockers</small></span><ExternalLink aria-hidden="true" />
            </a>}
            {!hasAction && readinessTone === "success" && <div className="pull-context-action action-success"><CircleCheck aria-hidden="true" /><span><strong>Ready to merge</strong><small>Checks pass, review is approved, and conversations are resolved.</small></span></div>}
            {!hasAction && readinessTone !== "success" && <div className="pull-context-action action-pending"><Clock3 aria-hidden="true" /><span><strong>{pendingChecks.length > 0 ? `${pendingChecks.length} checks still running` : reviewNeedsAttention ? reviewLabel : "GitHub status is still settling"}</strong><small>No failing signal requires action right now.</small></span></div>}
          </section>

          {changed?.status === "changed" && <section className="pull-context-changed">
            <header><Clock3 aria-hidden="true" /><span><strong>Changed since your review</strong><small>{changed.commits} {changed.commits === 1 ? "commit" : "commits"} · +{changed.additions} −{changed.deletions}</small></span></header>
            <ul>{changed.files.slice(0, 4).map((file) => <li key={file.path}><span>{file.path}</span><small>+{file.additions} −{file.deletions}</small></li>)}</ul>
            {changed.files.length > 4 && <small>+{changed.files.length - 4} more changed files</small>}
          </section>}

          <section className="pull-context-people">
            <div><small>Author</small><strong>@{pull.author}</strong></div>
            <div><small>Requested reviewers</small><strong>{review?.requestedReviewers.map((login) => `@${login}`).join(", ") || pull.requestedReviewers.map((login) => `@${login}`).join(", ") || "None"}</strong></div>
            <div><small>Approved by</small><strong>{review?.approvers.map((login) => `@${login}`).join(", ") || "Nobody yet"}</strong></div>
          </section>
        </div>

        <footer className="pull-context-popover-footer">
          <span>{pull.baseRef || "base"} ← {pull.headRef || "head"} · updated {displayDate(pull.updatedAt)}</span>
          <a href={pull.url} rel="noreferrer" target="_blank">Open PR <ExternalLink aria-hidden="true" /></a>
        </footer>
      </PopoverContent>
    </Popover>
  );
}

export function ReviewPage({
  descriptor,
  prefetchDescriptor,
  stackDescriptors = [descriptor],
}: {
  descriptor: FixtureDescriptor;
  prefetchDescriptor?: FixtureDescriptor;
  stackDescriptors?: FixtureDescriptor[];
}) {
  usePageMetadata(
    `#${descriptor.pullNumber} ${descriptor.title} · CHEW`,
    `Review ${descriptor.repository}#${descriptor.pullNumber} in CHEW.`,
  );
  const [loaded, setLoaded] = useState<LoadedState>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    const started = performance.now();
    void loadReviewBundle(descriptor)
      .then(async ({ config, files, glossary, guide }) => {
        const compilation = await compileCodeMap(guide, files, config);
        const errors = compilation.diagnostics.filter((entry) => entry.severity === "error");
        if (errors.length > 0) {
          throw new Error(`Chewfile compilation failed: ${errors.map((entry) => entry.message).join(" ")}`);
        }
        await cacheGuide(descriptor, guide, config);
        await cacheFiles(descriptor.id, files);
        if (!cancelled) {
          setLoaded({ config, glossary, guide, files, compilation, source: "network" });
          await db.performance.add({
            id: crypto.randomUUID(), fixtureId: descriptor.id, name: "compiled-review-load",
            valueMs: performance.now() - started, createdAt: new Date().toISOString(),
          });
        }
      })
      .catch(async (reason: unknown) => {
        const cached = await db.fixtures.get(descriptor.id);
        if (!cancelled && cached?.files) {
          const config = cached.config ?? defaultChewConfig;
          const compilation = await compileCodeMap(cached.guide, cached.files, config);
          const errors = compilation.diagnostics.filter((entry) => entry.severity === "error");
          if (errors.length === 0) {
            setLoaded({ config, guide: cached.guide, files: cached.files, compilation, source: "cache" });
            return;
          }
        }
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not compile this Chewfile");
      });
    return () => { cancelled = true; };
  }, [descriptor]);

  useEffect(() => {
    if (!loaded || !prefetchDescriptor) return;
    let cancelled = false;
    const prefetch = () => {
      void loadReviewBundle(prefetchDescriptor)
        .then(async ({ config, files, guide }) => {
          if (cancelled) return;
          await cacheGuide(prefetchDescriptor, guide, config);
          await cacheFiles(prefetchDescriptor.id, files);
        })
        .catch(() => undefined);
    };
    if ("requestIdleCallback" in window) {
      const requestId = window.requestIdleCallback(prefetch, { timeout: 1_500 });
      return () => { cancelled = true; window.cancelIdleCallback(requestId); };
    }
    const timeoutId = globalThis.setTimeout(prefetch, 250);
    return () => { cancelled = true; globalThis.clearTimeout(timeoutId); };
  }, [loaded, prefetchDescriptor]);

  if (error) return <div className="fatal-state"><strong>Could not open this review.</strong><p>{error}</p><a href="/">Back</a></div>;
  if (!loaded) return <ReviewSkeleton pullNumber={descriptor.pullNumber} repository={descriptor.repository} />;
  return (
    <ReviewWorkspace
      compilation={loaded.compilation}
      config={loaded.config}
      descriptor={descriptor}
      glossary={loaded.glossary}
      files={loaded.files}
      guide={loaded.guide}
      stackDescriptors={stackDescriptors}
      source={loaded.source}
    />
  );
}

function StackProgress({ current, guide, parts, compact = false }: { current: FixtureDescriptor; guide: ReviewGuide; parts: FixtureDescriptor[]; compact?: boolean }) {
  const stack = guide.scope.stack;
  const sessions = useLiveQuery(() => db.sessions.toArray()) ?? [];
  if (stack.role === "standalone") return null;
  const descriptorByPull = new Map(parts.map((part) => [part.pullNumber, part]));
  const declaredParts = stack.context?.parts ?? parts.map((part) => ({
    pullNumber: part.pullNumber,
    title: part.title,
    position: part.scope.stack.position,
  }));
  const completed = new Map(sessions.filter((session) => session.completedAt).map((session) => [session.fixtureId, session]));
  return (
    <Popover>
      <PopoverTrigger render={<button className="stack-progress-trigger" type="button" />}>
        <GitBranch aria-hidden="true" />
        {!compact && <span>{stack.title ?? "PR stack"}</span>}
        <strong>{stack.position}/{stack.total}</strong>
        <ChevronDown aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" className="stack-progress-popover" side="bottom" sideOffset={7}>
        <PopoverHeader>
          <PopoverTitle>{stack.title ?? "Pull request stack"}</PopoverTitle>
          <PopoverDescription>Review in dependency order. Each PR publishes separately.</PopoverDescription>
        </PopoverHeader>
        <ol>{[...declaredParts].sort((left, right) => left.position - right.position).map((part) => {
          const descriptor = descriptorByPull.get(part.pullNumber);
          const session = descriptor ? completed.get(descriptor.id) : undefined;
          const isCurrent = part.pullNumber === current.pullNumber;
          const state = isCurrent ? "Current" : session ? session.reviewVerdict === "approve" ? "Approved" : "Reviewed" : part.position < stack.position ? "Earlier" : part.position === stack.position + 1 ? "Next" : "Waiting";
          const contents = <><span>{session ? "✓" : part.position}</span><div><strong>#{part.pullNumber} · {part.title}</strong><small>{state}</small></div></>;
          return <li className={isCurrent ? "current" : session ? "complete" : ""} key={part.pullNumber}>
            {descriptor && !isCurrent
              ? <a href={descriptor.reviewPath ?? `/review/${descriptor.id}`} onClick={(event) => handleLocalNavigation(event, descriptor.reviewPath ?? `/review/${descriptor.id}`)}>{contents}</a>
              : <div>{contents}</div>}
          </li>;
        })}</ol>
        {stack.context && <footer><strong>Shared context</strong><span><GuideProse text={stack.context.purpose} /></span></footer>}
      </PopoverContent>
    </Popover>
  );
}

const HandBackDialog = lazy(() => import("./HandBackDialog").then((module) => ({ default: module.HandBackDialog })));
// Terraform is a sheet over this page rather than a page of its own, so that
// going back and forth between a plan and the HCL that caused it costs a
// keystroke instead of two navigations. Lazy because most pull requests are
// not infrastructure.
const TerraformSheet = lazy(() => import("./TerraformSheet").then((module) => ({ default: module.TerraformSheet })));

function ReviewHeader({
  descriptor,
  guide,
  stackDescriptors,
  screen,
  pullUrl,
  authoringPath,
  onTerraform,
  pullDetails,
  pullContext,
  modeLabel,
  source,
  traversalReady,
  onOverview,
  onFinish,
  onHandBack,
  guideAvailable,
  guideFollowing,
  onToggleGuide,
  introNotReadyAction,
}: {
  descriptor: FixtureDescriptor;
  guide: ReviewGuide;
  stackDescriptors: FixtureDescriptor[];
  screen: ReviewScreen;
  pullUrl: string;
  authoringPath?: string;
  /** Present only for a repository CHEW manages terraform for. */
  onTerraform?: () => void;
  pullDetails?: HostedPullDetails;
  pullContext?: HostedReviewContext;
  modeLabel: string;
  source: LoadedState["source"];
  traversalReady: boolean;
  onOverview: () => void;
  onFinish: () => void;
  onHandBack?: () => void;
  guideAvailable: boolean;
  guideFollowing: boolean;
  onToggleGuide: () => void;
  introNotReadyAction?: ReactNode;
}) {
  const badgeFacts = {
    repository: descriptor.repository,
    pullNumber: descriptor.pullNumber,
    author: descriptor.authorLogin ?? pullDetails?.author,
    additions: descriptor.stats.additions,
    deletions: descriptor.stats.deletions,
    changedFiles: descriptor.stats.changedFiles,
  };
  return (
    <header className="review-header">
      <div className="review-header-start"><a aria-label="CHEW Home" className="brand review-header-brand" href="/" onClick={(event) => handleLocalNavigation(event, "/")}><BrandMark /><strong>CHEW</strong></a><AppNavigationControls /></div>
      <div className="review-header-identity">
        <StackProgress compact current={descriptor} guide={guide} parts={stackDescriptors} />
        <a className="review-header-pr" href={pullUrl} rel="noreferrer" target="_blank" title="Open pull request on GitHub">
          <PullBadge facts={badgeFacts} size="md" />
          <span className="review-header-title">{descriptor.title}</span>
        </a>
      </div>
      <div className="review-header-actions">
        {guideAvailable && (
          <Tooltip>
            <TooltipTrigger render={
              <button
                aria-pressed={guideFollowing}
                className={`review-guide-toggle${guideFollowing ? " is-following" : ""}`}
                onClick={onToggleGuide}
                type="button"
              />
            }><Compass aria-hidden="true" /><span>{guideFollowing ? "Following guide" : "Follow the guide"}</span></TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={5}>{guideFollowing ? "Leave the walkthrough and see the whole diff" : "Walk the author's review order"} <kbd>G</kbd></TooltipContent>
          </Tooltip>
        )}
        {screen !== "intro" && <Tooltip><TooltipTrigger render={<button aria-label="Open PR overview" className="review-header-icon" onClick={onOverview} type="button" />}><Info aria-hidden="true" /></TooltipTrigger><TooltipContent side="bottom" sideOffset={5}>PR overview <kbd>O</kbd></TooltipContent></Tooltip>}
        {pullDetails && <PullContextDisclosure context={pullContext} pull={pullDetails} />}
        {onTerraform && <Tooltip><TooltipTrigger render={<button aria-label="Open the terraform panel" className="review-header-terraform" onClick={onTerraform} type="button" />}><Layers aria-hidden="true" /><span>Terraform</span></TooltipTrigger><TooltipContent side="bottom" sideOffset={5}>Projects, plans and locks for this PR <kbd>T</kbd></TooltipContent></Tooltip>}
        <Popover>
          <PopoverTrigger render={<button aria-label="More review options" className="review-header-icon" type="button" />}><MoreHorizontal aria-hidden="true" /></PopoverTrigger>
          <PopoverContent align="end" className="review-header-menu" side="bottom" sideOffset={7}>
            <PopoverHeader><PopoverTitle>Review options</PopoverTitle><PopoverDescription>Context and workspace preferences.</PopoverDescription></PopoverHeader>
            <a href={pullUrl} rel="noreferrer" target="_blank"><ExternalLink aria-hidden="true" /><span><strong>Open on GitHub</strong><small>{repositoryShortName(descriptor.repository)} #{descriptor.pullNumber}</small></span></a>
            {authoringPath && <a href={authoringPath} onClick={(event) => handleLocalNavigation(event, authoringPath)}><FilePenLine aria-hidden="true" /><span><strong>Refine reviewer guide</strong><small>Annotate context and regenerate with AI</small></span></a>}
            {onHandBack && <button className="review-header-hand-back" onClick={onHandBack} type="button"><Hand aria-hidden="true" /><span><strong>Not for me</strong><small>Release the claim and return it to the queue</small></span></button>}
            <div className="review-header-appearance"><span><strong>Appearance</strong><small>Auto, light, or dark</small></span><ThemeToggle /></div>
            <dl><div><dt>Mode</dt><dd>{modeLabel}</dd></div>{source === "network" && <div><dt>Source</dt><dd>Local fixture</dd></div>}</dl>
            {introNotReadyAction && <div className="review-header-not-ready">{introNotReadyAction}</div>}
          </PopoverContent>
        </Popover>
        {screen === "layer" && <Tooltip><TooltipTrigger render={<button aria-label={traversalReady ? "Finish review" : "Open review report"} className={`review-header-finish${traversalReady ? " ready" : ""}`} onClick={onFinish} type="button" />}><ClipboardCheck aria-hidden="true" /><span>{traversalReady ? "Finish" : "Review"}</span></TooltipTrigger><TooltipContent side="bottom" sideOffset={5}>{traversalReady ? "Finish review" : "Open review report"} <kbd>⌘→</kbd></TooltipContent></Tooltip>}
      </div>
    </header>
  );
}

function layerIsComplete(layer: GuideLayer, progress: ProgressProjection) {
  const status = progress.layerStatuses[layer.id];
  return status === "done" || status === "question" || status === "problem";
}

function tourStopsForLayer(guide: ReviewGuide, compilation: CodeMapCompilation, layer: GuideLayer) {
  return layer.items.flatMap((item) =>
    item.regionIds.flatMap((regionId) => buildTourStops(resolveGuideItem(guide, compilation, item, regionId))),
  );
}

function OverviewDialog({ compilation, config, guide, onClose }: { compilation: CodeMapCompilation; config: ChewConfig; guide: ReviewGuide; onClose: () => void }) {
  const presentation = reviewPresentation(guide, compilation.summary);
  type OverviewTab = "context" | "change" | "review" | "flow";
  const tabs: Array<{ id: OverviewTab; label: string }> = [
    { id: "context", label: "Context" },
    { id: "change", label: "Change" },
    { id: "review", label: "Review" },
  ];
  if (presentation === "guided" && guide.overview.mermaid) tabs.push({ id: "flow", label: "Flow" });
  const [activeTab, setActiveTab] = useState<OverviewTab>("context");
  return (
    <div className="overview-backdrop" role="presentation">
      <section aria-labelledby="overview-dialog-title" aria-modal="true" className="overview-dialog" role="dialog">
        <header>
          <div><span className="quiet-label">PR overview</span><h2 id="overview-dialog-title">{guide.overview.title}</h2></div>
          <button aria-label="Close overview" onClick={onClose} type="button">×</button>
        </header>
        <div aria-label="PR overview sections" className="overview-dialog-tabs" role="tablist">
          {tabs.map((tab) => (
            <button aria-selected={activeTab === tab.id} key={tab.id} onClick={() => setActiveTab(tab.id)} role="tab" type="button">{tab.label}</button>
          ))}
        </div>
        <div className="overview-dialog-page">
          {activeTab === "context" && <>
            {guide.overview.orientation && <article className="overview-dialog-orientation">
              <span>{guide.overview.orientation.subject}</span>
              <p><GuideProse text={guide.overview.orientation.definition} /></p>
              <small><GuideProse text={guide.overview.orientation.purpose} /></small>
            </article>}
            {(guide.overview.problem || guide.overview.approach) && <div className="overview-dialog-context">
              {guide.overview.problem && <article><span>Problem</span><p><GuideProse text={guide.overview.problem} /></p></article>}
              {guide.overview.approach && <article><span>Approach</span><p><GuideProse text={guide.overview.approach} /></p></article>}
            </div>}
          </>}
          {activeTab === "change" && <>
            <section className="overview-dialog-list">
              <span>{presentation === "guided" ? "Inside this PR" : "Change"}</span>
              <ul>{summaryItems(guide).map((item, index) => <li key={item.label ?? item.detail ?? index}>{item.label ? <strong><GuideProse text={item.label} /></strong> : null}{item.detail ? <GuideProse text={item.detail} /> : null}</li>)}</ul>
            </section>
            <div className={`overview-dialog-summary${presentation === "guided" ? "" : " overview-dialog-summary-compact"}`}>
              {guide.overview.today && <article><span>Today</span><p><GuideProse text={guide.overview.today} /></p></article>}
              {guide.overview.after && <article><span>After this change</span><p><GuideProse text={guide.overview.after} /></p></article>}
            </div>
          </>}
          {activeTab === "review" && <>
            <div className="overview-dialog-summary overview-dialog-summary-compact">
              <article className="overview-dialog-risk"><span>Main danger</span><p><GuideProse text={guide.overview.mainRisk} /></p></article>
            </div>
            {guide.overview.reviewFocus.length > 0 && <section className="overview-dialog-list overview-dialog-review-focus">
              <span>Review thrust</span>
              <ul>{guide.overview.reviewFocus.map((item) => <li key={item}><GuideProse text={item} /></li>)}</ul>
            </section>}
            {presentation !== "fast-lane" && <ChangeProfile compilation={compilation} config={config} />}
          </>}
          {activeTab === "flow" && guide.overview.mermaid && <MermaidDiagram source={guide.overview.mermaid} />}
        </div>
      </section>
    </div>
  );
}

export function ReviewWorkspace({
  compilation,
  config,
  descriptor,
  files,
  guide,
  hostedConnectionPending = false,
  hostedSessionId,
  onNeedPaths,
  pullContext,
  pullDetails,
  reviewAccess,
  stackDescriptors = [descriptor],
  source,
  warnings = [],
  reviewKind = "guided",
}: {
  compilation: CodeMapCompilation;
  config: ChewConfig;
  descriptor: FixtureDescriptor;
  files: FixtureFiles;
  glossary?: Glossary;
  guide: ReviewGuide;
  hostedConnectionPending?: boolean;
  hostedSessionId?: string;
  onNeedPaths?: (paths: string[]) => void;
  pullContext?: HostedReviewContext;
  pullDetails?: HostedPullDetails;
  reviewAccess?: ReviewAccess;
  stackDescriptors?: FixtureDescriptor[];
  source: LoadedState["source"];
  warnings?: Array<{ code: string; message: string }>;
  reviewKind?: "guided" | "description" | "unguided";
}) {
  const firstLayer = guide.layers[0]!;
  const pullUrl = descriptor.url ?? `https://github.com/${descriptor.repository}/pull/${descriptor.pullNumber}`;
  const unguided = reviewKind === "unguided";
  const presentation = unguided ? "fast-lane" : reviewPresentation(guide, compilation.summary);
  // The PR description is the complete pre-review briefing. It stays one page regardless of
  // change size, novelty, or authored guide density.
  const introPages = unguided ? 0 : 1;
  const fastLane = presentation === "fast-lane";
  const selfReview = reviewAccess?.role === "author";
  const authoringPath = selfReview && source === "github" && reviewKind === "guided"
    ? guideAuthoringPath(descriptor.repository, descriptor.pullNumber)
    : undefined;
  const terraformManaged = useTerraformPanel(descriptor.repository, descriptor.pullNumber, source === "github");
  // The selection is fetched as soon as CHEW knows this is an infrastructure
  // pull request, so the sheet paints from memory whenever it is opened. It is
  // one cached request and nobody waits on it.
  useEffect(() => {
    if (terraformManaged) prefetchTerraformSelection(descriptor.repository, descriptor.pullNumber);
  }, [descriptor.pullNumber, descriptor.repository, terraformManaged]);
  const landingPath = reviewLandingPath(reviewAccess?.role);
  const completedReviewPath = (selfReviewed = false) => {
    const query = new URLSearchParams({
      chewed: descriptor.id,
      chewedHead: guide.source.headSha,
      // The pull identity travels too: queue entries whose recorded head does
      // not match the guide's (local fixtures, or a briefly stale snapshot)
      // must still stop offering the review that was just finished.
      chewedPull: `${descriptor.repository}#${descriptor.pullNumber}`,
    });
    if (selfReviewed) query.set("selfReviewed", "1");
    return `${landingPath}?${query}`;
  };
  const allowedOutcomeSet = useMemo(
    () => new Set<ReviewVerdict>(reviewAccess?.allowedOutcomes ?? verdictOptions.map(({ id }) => id)),
    [reviewAccess],
  );
  const availableVerdictOptions = useMemo(
    () => verdictOptions
      .filter(({ id }) => allowedOutcomeSet.has(id))
      .map((option) => {
        if (!selfReview) return option;
        if (option.id === "comment") {
          return { ...option, label: "Publish comments", description: "Send notes and inline comments without a verdict." };
        }
        if (option.id === "not-ready") {
          return { ...option, label: "Needs more work", description: "Record what you want to fix before asking for review." };
        }
        return option;
      }),
    [allowedOutcomeSet, selfReview],
  );
  const routeDefaults = useMemo(() => ({
    defaultLayerId: firstLayer.id,
    introPages,
    layerIds: guide.layers.map((layer) => layer.id),
  }), [firstLayer.id, guide.layers, introPages]);
  const initialPosition = useMemo(
    () => readReviewPosition(window.location.search, routeDefaults),
    [routeDefaults],
  );
  const [screen, setScreen] = useState<ReviewScreen>(initialPosition.screen);
  // Until the stored session position has been read, rendering would paint the
  // intro and then jump to the restored layer one tick later.
  const [restoring, setRestoring] = useState(true);
  const [introPage, setIntroPage] = useState(initialPosition.screen === "intro" ? initialPosition.page : 0);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [handBackOpen, setHandBackOpen] = useState(false);
  // `?terraform=1` is what the old `/terraform/...` route now redirects to, so
  // a bookmark or a link in a comment still opens straight onto the panel —
  // over the review page, with the diff already behind it.
  const [terraformOpen, setTerraformOpen] = useState(
    () => new URLSearchParams(window.location.search).has("terraform"),
  );
  const [following, setFollowing] = useState(() => localStorage.getItem("chew:review:guide") === "follow");
  const followGuide = useCallback((next: boolean) => {
    setFollowing(next);
    localStorage.setItem("chew:review:guide", next ? "follow" : "whole-diff");
  }, []);
  const [notReadyOpen, setNotReadyOpen] = useState(false);
  const [notReadySummary, setNotReadySummary] = useState("");
  const [celebrating, setCelebrating] = useState(false);
  const [activeLayerId, setActiveLayerId] = useState(
    initialPosition.screen === "intro" ? firstLayer.id : initialPosition.layerId ?? firstLayer.id,
  );
  const [activeFocusId, setActiveFocusId] = useState(
    initialPosition.screen === "intro" ? undefined : initialPosition.focusId,
  );
  const [positionRevision, setPositionRevision] = useState(0);
  const [completionReturnIndex, setCompletionReturnIndex] = useState(() => {
    if (initialPosition.screen === "intro") return guide.layers.length - 1;
    const index = guide.layers.findIndex((layer) => layer.id === initialPosition.layerId);
    return index < 0 ? guide.layers.length - 1 : index;
  });
  const [reviewVerdict, setReviewVerdict] = useState<ReviewVerdict>();
  const [outcomeSummary, setOutcomeSummary] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publicationError, setPublicationError] = useState<string>();
  const review = useReviewSession(descriptor.id, guide, hostedSessionId);
  const activeReviewSeconds = useActiveReviewTime(
    sessionIdFor(descriptor.id, guide.source.headSha),
    Boolean(hostedSessionId && !review.session?.completedAt),
  );
  const progressRef = useRef(review.progress);
  progressRef.current = review.progress;
  const replyToThread = useCallback(async (commentId: number, body: string) => {
    if (!hostedSessionId) throw new Error("Thread replies require a live GitHub review.");
    return replyToHostedReviewThread({
      sessionId: hostedSessionId,
      headSha: guide.source.headSha,
      commentId,
      body,
    });
  }, [guide.source.headSha, hostedSessionId]);
  const changedSincePaths = useMemo(
    () => new Set(pullContext?.changedSinceReview.status === "changed"
      ? pullContext.changedSinceReview.files.map((file) => file.path)
      : []),
    [pullContext],
  );
  const firstLayerStatus = review.progress.layerStatuses[firstLayer.id] ?? "unseen";
  const allTourStops = useMemo(
    () => {
      const stops = guide.layers.flatMap((layer) =>
        layer.items.flatMap((item) =>
          item.regionIds.flatMap((regionId) =>
            buildTourStops(resolveGuideItem(guide, compilation, item, regionId)),
          ),
        ),
      );
      return [...new Map(stops.map((stop) => [stop.stopId, stop])).values()];
    },
    [compilation, guide],
  );
  const traversalReady = review.progress.canComplete || (
    allTourStops.length > 0 && allTourStops.every((stop) => review.progress.focusStopStatuses[stop.stopId] === "visited")
  );

  useEffect(() => {
    void ensureSession(descriptor.id, guide.source.headSha, firstLayer.id).then((session) => {
      setRestoring(false);
      if (hasExplicitReviewPosition()) return;
      const layerIndex = Math.max(0, guide.layers.findIndex((layer) => layer.id === session.activeLayerId));
      const layer = guide.layers[layerIndex] ?? firstLayer;
      setActiveLayerId(layer.id);
      if (!session.activeFocusId && !session.layerViewStates?.[layer.id]) return;
      setActiveFocusId(session.activeFocusId);
      setCompletionReturnIndex(layerIndex);
      setScreen("layer");
      setPositionRevision((current) => current + 1);
      writeReviewPosition({ screen: "layer", layerId: layer.id, focusId: session.activeFocusId }, { notify: false, replace: true });
    }).catch(() => setRestoring(false));
  }, [descriptor.id, firstLayer, guide.layers, guide.source.headSha]);

  useEffect(() => {
    const restoreFromUrl = () => {
      const position = readReviewPosition(window.location.search, routeDefaults);
      setPositionRevision((current) => current + 1);
      setScreen(position.screen);
      if (position.screen === "intro") {
        setIntroPage(position.page);
        return;
      }
      const index = Math.max(0, guide.layers.findIndex((layer) => layer.id === position.layerId));
      const layer = guide.layers[index] ?? firstLayer;
      setActiveLayerId(layer.id);
      setActiveFocusId(position.focusId);
      setCompletionReturnIndex(index);
      if (position.screen === "layer") {
        void review.setActiveLayer(layer.id, position.focusId);
        if ((progressRef.current.layerStatuses[layer.id] ?? "unseen") === "unseen") {
          void review.setLayerStatus(layer.id, "in-progress");
        }
      }
    };
    // Restore the in-review position before App's route listener rerenders the
    // page. Otherwise browser Forward can update the URL while leaving the
    // previous focal window selected.
    window.addEventListener("popstate", restoreFromUrl, { capture: true });
    return () => window.removeEventListener("popstate", restoreFromUrl, { capture: true });
  }, [firstLayer, guide.layers, review.setActiveLayer, review.setLayerStatus, routeDefaults]);

  useEffect(() => {
    if (!review.session) return;
    setReviewVerdict(
      review.session.reviewVerdict && allowedOutcomeSet.has(review.session.reviewVerdict)
        ? review.session.reviewVerdict
        : undefined,
    );
    setOutcomeSummary(review.session.outcomeSummary ?? "");
  }, [allowedOutcomeSet, review.session?.id]);

  useEffect(() => {
    // Never write before the stored events have loaded: `unseen` during the
    // loading window is "unknown", and writing then would clobber a recorded
    // done/question/problem status with a fresh in-progress on every mount.
    if (review.hydrated && fastLane && firstLayerStatus === "unseen") {
      void review.setLayerStatus(firstLayer.id, "in-progress");
    }
  }, [fastLane, firstLayer.id, firstLayerStatus, review.hydrated, review.setLayerStatus]);

  const activeIndex = Math.max(0, guide.layers.findIndex((layer) => layer.id === activeLayerId));
  const activeLayer = guide.layers[activeIndex] ?? firstLayer;
  const linkedPrompts = guide.reviewPrompts.filter((prompt) => activeLayer.promptIds.includes(prompt.id));
  const saveActiveLayerViewState = useCallback(
    (state: Omit<ReviewLayerViewState, "updatedAt">) => review.saveLayerViewState(activeLayer.id, state),
    [activeLayer.id, review.saveLayerViewState],
  );
  const storedActiveLayerViewState = review.session?.layerViewStates?.[activeLayer.id];
  const initialLayerViewState = activeFocusId && review.session?.activeFocusId && activeFocusId !== review.session.activeFocusId
    ? undefined
    : storedActiveLayerViewState;

  const selectLayer = useCallback(
    (index: number, entry: "start" | "end" = "start", requestedFocusId?: string) => {
      const layer = guide.layers[index];
      if (!layer) return;
      if (onNeedPaths) {
        const regions = new Map(guide.codeMap.regions.map((region) => [region.id, region]));
        const paths = [layer, guide.layers[index + 1]].flatMap((candidate) =>
          candidate?.items.flatMap((item) =>
            item.regionIds.map((id) => regions.get(id)?.path).filter(Boolean) as string[]
          ) ?? [],
        );
        onNeedPaths([...new Set(paths)]);
      }
      const stops = tourStopsForLayer(guide, compilation, layer);
      const focusId = requestedFocusId ?? (entry === "end" ? stops.at(-1)?.stopId : stops[0]?.stopId);
      setActiveLayerId(layer.id);
      setActiveFocusId(focusId);
      setScreen("layer");
      writeReviewPosition({ screen: "layer", layerId: layer.id, focusId }, { notify: false });
      void review.setActiveLayer(layer.id, focusId);
      if ((review.progress.layerStatuses[layer.id] ?? "unseen") === "unseen") {
        void review.setLayerStatus(layer.id, "in-progress");
      }
      window.scrollTo({ top: 0 });
    },
    [compilation, guide, onNeedPaths, review],
  );

  const selectCodeTarget = useCallback((target: ChewCodeTarget) => {
    const candidates = guide.layers.flatMap((candidateLayer, layerIndex) =>
      tourStopsForLayer(guide, compilation, candidateLayer)
        .filter((stop) => stop.path === target.path)
        .map((stop) => ({ layerIndex, stop })),
    ).sort((left, right) => {
      if (!target.startLine) return left.layerIndex - right.layerIndex;
      const leftDistance = target.startLine < left.stop.startLine ? left.stop.startLine - target.startLine : target.startLine > left.stop.endLine ? target.startLine - left.stop.endLine : 0;
      const rightDistance = target.startLine < right.stop.startLine ? right.stop.startLine - target.startLine : target.startLine > right.stop.endLine ? target.startLine - right.stop.endLine : 0;
      return leftDistance - rightDistance || left.layerIndex - right.layerIndex;
    });
    if (candidates[0]) selectLayer(candidates[0].layerIndex, "start", candidates[0].stop.stopId);
  }, [compilation, guide, selectLayer]);

  const selectableCodePaths = useMemo(() => new Set(
    guide.layers.flatMap((layer) => tourStopsForLayer(guide, compilation, layer).map((stop) => stop.path)),
  ), [compilation, guide]);

  const selectIntroPage = useCallback((page: number) => {
    const boundedPage = Math.max(0, Math.min(Math.max(0, introPages - 1), page));
    setIntroPage(boundedPage);
    setScreen("intro");
    writeReviewPosition({ screen: "intro", page: boundedPage }, { notify: false });
    window.scrollTo({ top: 0 });
  }, [introPages]);

  const begin = () => {
    rememberStackContext(descriptor, guide.scope.stack.context?.revision);
    const nextIndex = guide.layers.findIndex((layer) => !layerIsComplete(layer, review.progress));
    selectLayer(nextIndex < 0 ? guide.layers.length - 1 : nextIndex);
  };

  const openReceipt = useCallback(() => {
    setCompletionReturnIndex(activeIndex);
    setScreen("complete");
    writeReviewPosition({
      screen: "complete",
      layerId: activeLayer.id,
      focusId: activeFocusId,
    }, { notify: false });
    window.scrollTo({ top: 0 });
  }, [activeFocusId, activeIndex, activeLayer.id]);

  const next = () => {
    if (activeIndex >= guide.layers.length - 1) {
      openReceipt();
      return;
    }
    selectLayer(activeIndex + 1, "start");
  };

  const previous = () => {
    if (activeIndex === 0) {
      if (introPages === 0) navigate(landingPath);
      else {
        selectIntroPage(introPages - 1);
      }
      return;
    }
    selectLayer(activeIndex - 1, "end");
  };

  const exportSession = async () => {
    const [session, events, drafts, samples] = await Promise.all([
      db.sessions.get(sessionIdFor(descriptor.id, guide.source.headSha)),
      db.events.where("sessionId").equals(sessionIdFor(descriptor.id, guide.source.headSha)).toArray(),
      db.drafts.where("sessionId").equals(sessionIdFor(descriptor.id, guide.source.headSha)).toArray(),
      db.performance.where("fixtureId").equals(descriptor.id).toArray(),
    ]);
    const blob = new Blob([
      JSON.stringify({ descriptor, guideHeadSha: guide.source.headSha, session, events, drafts, samples }, null, 2),
    ], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${descriptor.id}-review-session.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const draftCount = review.drafts.filter((draft) => draft.body.trim()).length;
  const outcomeNeedsReason = reviewVerdict !== undefined && reviewVerdict !== "approve";
  const outcomeReady = Boolean(
    reviewVerdict && allowedOutcomeSet.has(reviewVerdict) && (!outcomeNeedsReason || outcomeSummary.trim() || draftCount > 0),
  );

  const openNotReady = () => {
    setNotReadySummary(reviewVerdict === "not-ready" ? outcomeSummary : "");
    setPublicationError(undefined);
    setOverviewOpen(false);
    setNotReadyOpen(true);
  };

  const returnFromCompletion = () => {
    if (completionReturnIndex < 0) {
      selectIntroPage(Math.max(0, introPages - 1));
      return;
    }
    selectLayer(completionReturnIndex, "end", activeFocusId);
  };

  const completeReplay = async (
    verdict: ReviewVerdict | undefined = reviewVerdict,
    summary = outcomeSummary,
  ): Promise<boolean> => {
    if (review.session?.completedAt) {
      navigate(completedReviewPath());
      return true;
    }
    if (verdict && !allowedOutcomeSet.has(verdict)) {
      setPublicationError("PR authors can publish comments from CHEW, but cannot approve or request changes on their own PR.");
      return false;
    }
    const ready = Boolean(verdict && (verdict === "approve" || summary.trim() || draftCount > 0));
    if (!verdict || !ready) return false;
    if (source === "github" && !hostedSessionId) {
      setPublicationError(hostedConnectionPending
        ? "CHEW is still connecting this review to GitHub. Try again in a moment."
        : "This review is not connected to GitHub and cannot be published yet.");
      return false;
    }
    const startedAt = review.session?.startedAt ? Date.parse(review.session.startedAt) : Date.now();
    if (hostedSessionId) {
      setPublishing(true);
      setPublicationError(undefined);
      try {
        const nonLineDrafts = review.drafts.filter((draft) => !lineAnchorForDraft(draft) && draft.body.trim());
        const bodyParts = [summary.trim()];
        if (nonLineDrafts.length > 0) {
          bodyParts.push(nonLineDrafts.map((draft) => `- ${draft.body.trim()}`).join("\n"));
        }
        let body = bodyParts.filter(Boolean).join("\n\n");
        if (verdict === "request-changes" && !body) body = "Requested changes are described inline.";
        const activeSeconds = await activeReviewSeconds();
        await publishHostedReview({
          sessionId: hostedSessionId,
          repository: descriptor.repository,
          pullNumber: descriptor.pullNumber,
          headSha: guide.source.headSha,
          outcome: verdict,
          selfReview,
          body,
          activeSeconds,
          comments: review.drafts.flatMap((draft) => {
            const anchor = lineAnchorForDraft(draft);
            return anchor && draft.body.trim()
              ? [hostedInlineComment(anchor, draft.body.trim(), draftCommentKind(draft))]
              : [];
          }),
        });
        await review.completeReplay(Date.now() - startedAt, verdict, summary);
        setCelebrating(true);
        window.setTimeout(() => navigate(completedReviewPath()), 180);
        return true;
      } catch (reason) {
        setPublicationError(reason instanceof Error ? reason.message : "Could not publish the GitHub review.");
        return false;
      } finally {
        setPublishing(false);
      }
    }
    await review.completeReplay(Date.now() - startedAt, verdict, summary);
    setCelebrating(true);
    window.setTimeout(() => {
      navigate(completedReviewPath());
    }, 160);
    return true;
  };

  const attestAuthorSweep = async (): Promise<void> => {
    if (!selfReview || publishing) return;
    if (source === "github" && !hostedSessionId) {
      setPublicationError(hostedConnectionPending
        ? "CHEW is still connecting this self-review to GitHub. Try again in a moment."
        : "This self-review is not connected to GitHub and cannot be attested yet.");
      return;
    }
    if (!hostedSessionId) return;
    setPublishing(true);
    setPublicationError(undefined);
    const startedAt = review.session?.startedAt ? Date.parse(review.session.startedAt) : Date.now();
    try {
      const publishableDrafts = review.drafts.filter((draft) => draft.body.trim());
      if (publishableDrafts.length > 0) {
        const nonLineDrafts = publishableDrafts.filter((draft) => !lineAnchorForDraft(draft));
        await publishHostedReview({
          sessionId: hostedSessionId,
          repository: descriptor.repository,
          pullNumber: descriptor.pullNumber,
          headSha: guide.source.headSha,
          outcome: "comment",
          body: nonLineDrafts.map((draft) => `- ${draft.body.trim()}`).join("\n"),
          activeSeconds: await activeReviewSeconds(),
          comments: publishableDrafts.flatMap((draft) => {
            const anchor = lineAnchorForDraft(draft);
            return anchor ? [hostedInlineComment(anchor, draft.body.trim(), draftCommentKind(draft))] : [];
          }),
        });
      }
      await attestHostedAuthorSweep({
        sessionId: hostedSessionId,
        headSha: guide.source.headSha,
        markReady: true,
      });
      await review.completeReplay(Date.now() - startedAt, "comment", "Author sweep complete for this head.");
      setCelebrating(true);
      window.setTimeout(() => navigate(completedReviewPath(true)), 180);
    } catch (reason) {
      setPublicationError(reason instanceof Error ? reason.message : "Could not record the author sweep.");
    } finally {
      setPublishing(false);
    }
  };

  const submitNotReady = async () => {
    const summary = notReadySummary.trim();
    if (!summary || publishing) return;
    setReviewVerdict("not-ready");
    setOutcomeSummary(summary);
    await review.saveOutcomeDraft("not-ready", summary);
    if (await completeReplay("not-ready", summary)) setNotReadyOpen(false);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping = Boolean(target?.closest("input, textarea, select, [contenteditable='true']"));
      if (document.querySelector('[data-slot="dialog-content"][data-open]')) return;
      // The terraform sheet is over the page and handles its own keys,
      // including Escape. Letting the diff's shortcuts through underneath it
      // would move the reader's place in the code while they are reading a plan.
      if (terraformOpen) return;
      if (event.key === "Escape" && (overviewOpen || notReadyOpen || handBackOpen)) {
        event.preventDefault();
        setOverviewOpen(false);
        setNotReadyOpen(false);
        setHandBackOpen(false);
        return;
      }
      if (screen === "complete" && (event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        if (selfReview && !reviewVerdict && !publishing) void attestAuthorSweep();
        else if (outcomeReady && !publishing) void completeReplay();
        return;
      }
      if (
        screen !== "complete" &&
        allowedOutcomeSet.has("not-ready") &&
        !isTyping &&
        !overviewOpen &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        event.shiftKey &&
        event.key.toLowerCase() === "n"
      ) {
        event.preventDefault();
        openNotReady();
        return;
      }
      if (
        screen !== "intro" &&
        !isTyping &&
        !overviewOpen &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        event.key.toLowerCase() === "o"
      ) {
        event.preventDefault();
        setOverviewOpen(true);
        return;
      }
      if (!unguided && !isTyping && event.key.toLocaleLowerCase() === "g" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        followGuide(!following);
        return;
      }
      // Terraform is one key away from the code and one key back, which is the
      // whole point of it being a sheet.
      if (terraformManaged && !isTyping && event.key.toLowerCase() === "t" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setTerraformOpen(true);
        return;
      }
      if (screen === "layer") return;
      if (isTyping || overviewOpen || celebrating || event.altKey || event.ctrlKey || event.metaKey) return;
      if (screen === "intro" && event.key === "ArrowLeft") {
        event.preventDefault();
        if (introPage > 0) selectIntroPage(introPage - 1);
        else navigate(landingPath);
      }
      if (screen === "intro" && (event.key === "ArrowRight" || (event.key === "Enter" && !target?.closest("button, a")))) {
        event.preventDefault();
        if (introPage < introPages - 1) selectIntroPage(introPage + 1);
        else begin();
      }
      if (screen === "complete" && event.key === "ArrowLeft") {
        event.preventDefault();
        returnFromCompletion();
      }
      if (screen === "complete") {
        const selected = availableVerdictOptions.find((option) => option.key.toLowerCase() === event.key.toLowerCase());
        if (selected) {
          event.preventDefault();
          setReviewVerdict(selected.id);
          void review.saveOutcomeDraft(selected.id, outcomeSummary);
          return;
        }
      }
      if (screen === "complete" && (event.key === "ArrowRight" || (event.key === "Enter" && !target?.closest("button, a")))) {
        event.preventDefault();
        if (outcomeReady) void completeReplay();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [allowedOutcomeSet, availableVerdictOptions, celebrating, completionReturnIndex, following, followGuide, introPage, introPages, handBackOpen, landingPath, notReadyOpen, unguided, outcomeReady, outcomeSummary, overviewOpen, presentation, publishing, review, screen, selectIntroPage, selectLayer, terraformManaged, terraformOpen]);

  const currentStep = screen === "intro" ? 0 : screen === "complete" ? guide.layers.length : activeIndex + 1;
  usePageMetadata(
    `${screen === "layer" ? `${activeIndex + 1}/${guide.layers.length} · ` : screen === "complete" ? "Review receipt · " : ""}#${descriptor.pullNumber} ${descriptor.title} · CHEW`,
    screen === "complete"
      ? `Choose and publish the outcome for ${descriptor.repository}#${descriptor.pullNumber}.`
      : `Review ${descriptor.repository}#${descriptor.pullNumber}: ${descriptor.title}.`,
  );

  // Hold first paint until the stored session, events, and position have
  // loaded, so the page opens where the reviewer left off instead of flashing
  // the intro (with everything reading "not started") and then jumping.
  if (!review.session || !review.hydrated || restoring) {
    return <ReviewSkeleton pullNumber={descriptor.pullNumber} repository={descriptor.repository} />;
  }

  const modeLabel = selfReview
    ? `Self-review${unguided ? " · Full diff" : reviewKind === "description" ? " · PR guide" : ""}`
    : unguided ? "Full diff" : reviewKind === "description" ? "PR guide" : fastLane ? "Fast lane" : guide.mode === "replay" ? "Replay" : "Live";
  const notReadyAction = screen !== "complete" && allowedOutcomeSet.has("not-ready") ? (
    <Popover onOpenChange={(open) => {
      setNotReadyOpen(open);
      if (open) {
        setNotReadySummary(reviewVerdict === "not-ready" ? outcomeSummary : "");
        setPublicationError(undefined);
      }
    }} open={notReadyOpen}>
      <PopoverTrigger
        render={<button aria-label={selfReview ? "Mark PR as needing more work" : "Return PR as not ready"} className={screen === "layer" ? "toolbar-not-ready-button" : "review-menu-not-ready-button"} title={`${selfReview ? "Needs more work" : "Return PR as not ready"} · Shift+N`} type="button" />}
      >
        <CircleAlert aria-hidden="true" /><span>{selfReview ? "Needs work" : "Not ready"}</span><kbd>⇧N</kbd>
      </PopoverTrigger>
      <PopoverContent align="end" className="not-ready-popover" side={screen === "layer" ? "top" : "bottom"} sideOffset={7}>
        <PopoverHeader>
          <PopoverTitle>{selfReview ? "Keep working on this PR" : "Return as not ready"}</PopoverTitle>
          <PopoverDescription>{selfReview ? "Record what you want to fix before asking someone else to review." : "This requests changes on GitHub. Tell the author what must happen before review can continue."}</PopoverDescription>
        </PopoverHeader>
        <Textarea
          aria-label="Not ready reason"
          autoFocus
          onChange={(event) => setNotReadySummary(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
              event.preventDefault();
              void submitNotReady();
            }
          }}
          placeholder={selfReview ? "What still needs work?" : "What is missing?"}
          rows={3}
          value={notReadySummary}
        />
        {publicationError && <p className="review-publication-error" role="alert">{publicationError}</p>}
        <div className="not-ready-actions">
          <Button onClick={() => setNotReadyOpen(false)} size="sm" type="button" variant="ghost">Cancel</Button>
          <Button disabled={!notReadySummary.trim() || publishing || hostedConnectionPending} onClick={() => void submitNotReady()} size="sm" type="button" variant="destructive">
            {publishing ? "Saving…" : selfReview ? "Record and finish" : "Return now"}<kbd aria-hidden="true">⌘↵</kbd>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  ) : undefined;

  return (
    <div className="review-shell">
      <ReviewHeader authoringPath={authoringPath} onTerraform={terraformManaged ? () => setTerraformOpen(true) : undefined} descriptor={descriptor} guide={guide} introNotReadyAction={screen === "intro" ? notReadyAction : undefined} modeLabel={modeLabel} onFinish={openReceipt} guideAvailable={!unguided} guideFollowing={following} onToggleGuide={() => followGuide(!following)} onHandBack={hostedSessionId ? () => setHandBackOpen(true) : undefined} onOverview={() => setOverviewOpen(true)} pullContext={pullContext} pullDetails={pullDetails} pullUrl={pullUrl} screen={screen} source={source} stackDescriptors={stackDescriptors} traversalReady={traversalReady} />
      {terraformOpen && (
        <Suspense fallback={null}>
          <TerraformSheet
            onClose={() => setTerraformOpen(false)}
            pullNumber={descriptor.pullNumber}
            repository={descriptor.repository}
          />
        </Suspense>
      )}
      {handBackOpen && (
        <Suspense fallback={null}>
          <HandBackDialog
            headSha={guide.source.headSha}
            onClose={() => setHandBackOpen(false)}
            pullNumber={descriptor.pullNumber}
            repository={descriptor.repository}
          />
        </Suspense>
      )}
      {/* The briefing stands on its own; a progress track above it read as a
          stray artifact before the review had even started. */}
      {screen === "complete" && <ProgressHeader
          currentStep={currentStep}
          onSelectStep={(index) => selectLayer(index)}
          progress={review.progress}
          steps={guide.layers.map(({ id, title }) => ({ id, title }))}
        />}
      {warnings.length > 0 && (
        <div className="hosted-review-warning" role="status">
          {warnings.map((warning) => <span key={warning.code}>{warning.message}</span>)}
        </div>
      )}

      <main className={`guided-main${screen === "layer" ? " workspace-main" : ""}${screen === "intro" ? " intro-main" : ""}`}>
        {screen === "intro" && (
          <OverviewCard
            description={pullDetails?.body ?? descriptor.body}
            descriptor={descriptor}
            files={files}
            guide={guide}
            guided={reviewKind === "guided" && !unguided}
            onSelectCode={selectCodeTarget}
            onStart={begin}
            resuming={review.progress.layersComplete > 0}
            selectableCodePaths={selectableCodePaths}
          />
        )}

        {screen === "layer" && (
          <LayerView
            allTourStops={allTourStops}
            changedSincePaths={changedSincePaths}
            collapseByDefault={config.review.collapseByDefault}
            compilation={compilation}
            defaultFileOrder={config.review.defaultFileOrder}
            descriptor={descriptor}
            prompts={linkedPrompts}
            pullContext={pullContext}
            pullDetails={pullDetails}
            drafts={review.drafts}
            files={files}
            guide={guide}
            initialStopId={activeFocusId}
            initialViewState={initialLayerViewState}
            savedCollapsedPaths={storedActiveLayerViewState?.collapsedPaths}
            savedCollapsedPathsUpdatedAt={storedActiveLayerViewState?.updatedAt}
            positionRevision={positionRevision}
            layer={activeLayer}
            reviewMode={presentation}
            following={following}
            onFollowingChange={followGuide}
            unguided={unguided}
            onDraft={review.saveDraft}
            onDeleteDraft={review.deleteDraft}
            onFeedback={(itemId, message) => void review.saveFeedback(activeLayer.id, itemId, message)}
            onFocusStopVisited={(stopId) => void review.setFocusStopVisited(stopId)}
            onFocusChange={(focusId, source) => {
              // A passive scroll can update the address bar without feeding the new
              // focus back into LayerView. Explicit navigation still owns re-centering.
              if (source === "navigation") setActiveFocusId(focusId);
              void review.setActiveLayer(activeLayer.id, focusId);
              // Focus moves within a step replace the current history entry:
              // pushing one per focal stop made the browser Back button rewind
              // the review one code stop at a time. Steps still push, so Back
              // walks steps and then leaves the review.
              writeReviewPosition(
                { screen: "layer", layerId: activeLayer.id, focusId },
                { notify: false, replace: true },
              );
            }}
            onNext={next}
            onNeedFile={(path) => onNeedPaths?.([path])}
            onNeedFiles={(paths) => onNeedPaths?.(paths)}
            onSelectCode={selectCodeTarget}
            onViewStateChange={saveActiveLayerViewState}
            toolbarAction={notReadyAction}
            onOpenOverview={() => setOverviewOpen(true)}
            onOpenReceipt={openReceipt}
            onSelectStep={(index) => selectLayer(index)}
            onPrevious={previous}
            onReplyThread={hostedSessionId ? replyToThread : undefined}
            onRegionPresented={(regionId) => void review.setRegionStatus(regionId, "presented")}
            onStatus={(status: LayerStatus) => void review.setLayerStatus(activeLayer.id, status)}
            regionStatuses={review.progress.regionStatuses}
            focusStopStatuses={review.progress.focusStopStatuses}
            status={review.progress.layerStatuses[activeLayer.id] ?? "unseen"}
            stepNumber={activeIndex + 1}
            reviewSteps={guide.layers.map((candidate, index) => ({
              id: candidate.id,
              index,
              scrutiny: candidate.scrutiny,
              status: review.progress.layerStatuses[candidate.id] ?? "unseen",
              title: candidate.title,
            }))}
            totalSteps={guide.layers.length}
          />
        )}

        {screen === "complete" && (
          <section className={`completion-page${fastLane ? " completion-page-fast" : ""}`}>
            <header className="completion-heading">
              <span className="quiet-label">
                {unguided
                  ? "Unguided diff traversal finished"
                  : fastLane
                  ? "Fast lane walkthrough finished"
                  : review.progress.canComplete
                    ? "Walkthrough finished"
                    : "Walkthrough stopped with gaps"}
              </span>
              <h1>{unguided
                ? "Choose an outcome"
                : fastLane
                ? `${compilation.summary.changedLoc} changed ${compilation.summary.changedLoc === 1 ? "line" : "lines"} reviewed.`
                : "Choose an outcome"}</h1>
              <p>{selfReview
                ? "Confirm this exact version is ready, publish any notes, or record what still needs work."
                : unguided
                ? "CHEW presented the raw change without claiming that a semantic walkthrough covered its important risks."
                : fastLane
                ? guide.overview.mainRisk
                : "The receipt records coverage. The outcome records what should happen next."}</p>
            </header>

            <div className="completion-grid">
              <section aria-labelledby="review-outcome-title" className="review-outcome-card">
                <header>
                  <span>{selfReview ? "Self-review" : "Verdict"}</span>
                  <strong id="review-outcome-title">{selfReview ? "Is this version ready for someone else?" : "What should happen next?"}</strong>
                </header>
                <div aria-label="Review outcome" className="review-verdict-options" role="radiogroup">
                  {selfReview && (
                    <button
                      aria-checked={!reviewVerdict}
                      className="author-attestation-card"
                      data-selected={!reviewVerdict ? "true" : "false"}
                      onClick={() => {
                        setReviewVerdict(undefined);
                        setOutcomeSummary("");
                        void review.saveOutcomeDraft(undefined, "");
                      }}
                      role="radio"
                      type="button"
                    >
                      <Check aria-hidden="true" />
                      <div>
                        <strong>Ready for review</strong>
                        <p>Attest that you reviewed this exact head.</p>
                      </div>
                      <small>{pullDetails?.draft ? "Marks PR ready" : "Saves exact-head marker"}</small>
                    </button>
                  )}
                  {availableVerdictOptions.map((option) => (
                    <Button
                      aria-checked={reviewVerdict === option.id}
                      className="review-verdict-option"
                      data-selected={reviewVerdict === option.id ? "true" : "false"}
                      data-verdict={option.id}
                      key={option.id}
                      onClick={() => {
                        setReviewVerdict(option.id);
                        void review.saveOutcomeDraft(option.id, outcomeSummary);
                      }}
                      role="radio"
                      type="button"
                      variant="outline"
                    >
                      <span className="verdict-copy"><strong>{option.label}</strong><small>{option.description}</small></span>
                      <kbd>{option.key}</kbd>
                    </Button>
                  ))}
                </div>
                {reviewVerdict && <label className="review-outcome-summary">
                  <span>{reviewVerdict === "approve" ? "Review summary (optional)" : selfReview ? "Self-review note" : "Review summary"}</span>
                  <Textarea
                    onBlur={() => void review.saveOutcomeDraft(reviewVerdict, outcomeSummary)}
                    onChange={(event) => setOutcomeSummary(event.target.value)}
                    placeholder={
                      reviewVerdict === "request-changes" ? "What must change?"
                        : reviewVerdict === "comment" ? "Summarize the questions or non-blocking feedback."
                          : reviewVerdict === "not-ready" ? selfReview ? "What still needs work before requesting review?" : "What is missing before review can continue?"
                            : reviewVerdict === "approve" ? "Anything worth recording with the approval."
                              : "Select an outcome first."
                    }
                    rows={3}
                    value={outcomeSummary}
                  />
                </label>}
                <div className={`review-feedback-status${outcomeNeedsReason && !outcomeReady ? " needs-feedback" : ""}`}>
                  <span>{draftCount} inline {draftCount === 1 ? "draft" : "drafts"}</span>
                  {outcomeNeedsReason && !outcomeReady && <strong>Add a summary or inline comment.</strong>}
                </div>
                {publicationError && <p className="review-publication-error" role="alert">{publicationError}</p>}
                {!fastLane && <div className="completion-meta">
                  <span><strong>{review.progress.layersComplete}/{guide.layers.length}</strong> parts</span>
                  <span><strong>{Object.keys(review.progress.focusStopStatuses).length}/{allTourStops.length}</strong> focal stops</span>
                  <span><strong>{draftCount}</strong> comments</span>
                </div>}
              </section>

              {unguided ? (
                <section className="unguided-receipt">
                  <span>Unguided review</span>
                  <strong>{compilation.summary.expectedFiles} files · {compilation.summary.changedLoc.toLocaleString()} changed LOC</strong>
                  <p>CHEW presented the raw change. No Chewfile classified the business meaning, risks, or intentionally skimmed surface.</p>
                  {compilation.summary.unverifiedFiles > 0 && <small>{compilation.summary.unverifiedFiles} files had incomplete GitHub patch metadata; full source was still available.</small>}
                </section>
              ) : fastLane ? (
                <details className="completion-fast-receipt">
                  <summary><strong>Review Receipt</strong><span>See exactly what CHEW presented</span></summary>
                  <ReviewReceiptView compact compilation={compilation} config={config} guide={guide} progress={review.progress} />
                </details>
              ) : (
                <div className="completion-receipt-panel">
                  <ReviewReceiptView compact compilation={compilation} config={config} guide={guide} progress={review.progress} />
                </div>
              )}
            </div>

            <div className="completion-actions">
              <Button onClick={returnFromCompletion} type="button" variant="outline">← Return to review</Button>
              <div>
                <Button onClick={() => void exportSession()} type="button" variant="ghost">Export</Button>
                <Button
                  className="primary-button"
                  disabled={publishing || hostedConnectionPending || (!selfReview && !outcomeReady && !review.session?.completedAt) || (selfReview && Boolean(reviewVerdict) && !outcomeReady)}
                  onClick={() => void (selfReview && !reviewVerdict ? attestAuthorSweep() : completeReplay())}
                  type="button"
                >
                  {publishing
                    ? "Publishing…"
                    : review.session?.completedAt
                    ? "See next PR"
                    : guide.mode === "replay"
                      ? "Complete replay"
                      : selfReview
                        ? !reviewVerdict
                          ? pullDetails?.draft ? "Attest & mark ready" : "Attest current head"
                          : reviewVerdict === "not-ready" ? "Record needs work" : "Publish comments"
                        : "Publish review"}
                  <kbd aria-hidden="true">⌘↵</kbd>
                </Button>
              </div>
            </div>
            <button
              className="reset-review"
              onClick={() => {
                if (window.confirm("Clear local progress and drafts for this replay?")) {
                  void resetReview(descriptor.id, guide.source.headSha).then(() => window.location.reload());
                }
              }}
              type="button"
            >
              Reset local review
            </button>
          </section>
        )}
      </main>

      {overviewOpen && <OverviewDialog compilation={compilation} config={config} guide={guide} onClose={() => setOverviewOpen(false)} />}
      {celebrating && (
        <div className="chew-reward" role="status">
          <span>✓</span>
          <strong>{descriptor.stats.changedFiles > 50 ? "Tough Chew" : "PR chewed"}</strong>
        </div>
      )}
    </div>
  );
}
